import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  CarPostAnAdWidget,
  type CarPostAnAdWidgetHandle,
} from "@/components/car-post-ad/CarPostAnAdWidget";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";
import { restoreNativeSheetAfterDismissVeto } from "@/lib/native/restoreNativeSheetAfterDismissVeto";
import { useNativeSheetOpensExpanded } from "@/lib/native/useNativeSheetOpensExpanded";

interface CreateTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  isEditMode?: boolean;
  isCompletionMode?: boolean;
  title?: string;
  onTestSaved?: (updatedTest: any) => void;
}

export const CreateTestDialog = ({
  open,
  onOpenChange,
  initialData,
  isEditMode = false,
  isCompletionMode = false,
  title,
  onTestSaved,
}: CreateTestDialogProps) => {
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const ct = carTestLabels(language);
  const lp = listingPostLabels(language);
  const widgetRef = useRef<CarPostAnAdWidgetHandle>(null);
  const [hasProgress, setHasProgress] = useState(false);
  const [imageStepDirty, setImageStepDirty] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [isSavingImagesOnClose, setIsSavingImagesOnClose] = useState(false);

  useEffect(() => {
    const handleProgressUpdate = (event: Event) => {
      const ce = event as CustomEvent<{
        hasProgress?: boolean;
        imageStepDirty?: boolean;
      }>;
      setHasProgress(!!ce.detail?.hasProgress);
      setImageStepDirty(!!ce.detail?.imageStepDirty);
    };

    window.addEventListener(
      "carPostAdProgress",
      handleProgressUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "carPostAdProgress",
        handleProgressUpdate as EventListener,
      );
    };
  }, []);

  // Reset dirty flags when dialog opens so edit mode doesn't inherit stale "has progress"
  useEffect(() => {
    if (open) {
      setHasProgress(false);
      setImageStepDirty(false);
    }
  }, [open, initialData?.id, isEditMode]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && hasProgress) {
      setCloseConfirmOpen(true);
      return;
    }
    onOpenChange(newOpen);
  };

  const finishClose = () => {
    setCloseConfirmOpen(false);
    onOpenChange(false);
  };

  useEffect(() => {
    if (!closeConfirmOpen && open) {
      restoreNativeSheetAfterDismissVeto();
    }
  }, [closeConfirmOpen, open]);

  useNativeSheetOpensExpanded(open, isNative);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        beforeDismiss={() => {
          if (hasProgress) {
            setCloseConfirmOpen(true);
            return false;
          }
          return true;
        }}
      >
        <DialogContent
          expandable={isNative}
          sheetOpen={open}
          className={cn(
            "max-w-2xl p-0 sm:p-6 pt-2 sm:pt-6 pb-4 sm:pb-6",
            isNative &&
              "native-test-sheet native-sheet-expandable native-sheet-form max-w-none w-full rounded-t-2xl",
          )}
        >
          <div className="w-full max-w-full overflow-x-hidden px-3 sm:px-0">
            {/* Remount on each open so edit dirty-tracking starts clean (admin starts on image step). */}
            {open && (
              <CarPostAnAdWidget
                key={String(initialData?.id ?? "new")}
                ref={widgetRef}
                initialData={initialData}
                isEditMode={isEditMode}
                isCompletionMode={isCompletionMode}
                onTestSaved={onTestSaved}
                onOpenChange={onOpenChange}
                title={title}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <AlertDialogContent nested sheetOpen={closeConfirmOpen}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {imageStepDirty
                ? lp.widget.closeConfirmUnsavedImagesTitle
                : ct.dialogs.closeWithChangesTitle ?? ct.dialogs.closeWithChanges}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {imageStepDirty
                  ? lp.widget.closeConfirmUnsavedImagesBody
                  : ct.dialogs.closeWithChanges ||
                    "Alla ändringar kommer att gå förlorade. Är du säker?"}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>{lp.widget.cancel}</AlertDialogCancel>
            {imageStepDirty && (
              <Button
                type="button"
                variant="default"
                disabled={isSavingImagesOnClose}
                onClick={async () => {
                  setIsSavingImagesOnClose(true);
                  try {
                    await widgetRef.current?.saveImageStepIfDirty?.();
                    finishClose();
                  } finally {
                    setIsSavingImagesOnClose(false);
                  }
                }}
              >
                {isSavingImagesOnClose ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {lp.widget.saving}
                  </>
                ) : (
                  lp.widget.closeConfirmSaveImages
                )}
              </Button>
            )}
            <AlertDialogAction
              onClick={() => finishClose()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {imageStepDirty
                ? lp.widget.closeConfirmDiscard
                : ct.dialogs.confirmClose ?? "Stäng"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
